import React from 'react';
import type { BrandLogoComponent } from '../../../types/builder';

interface BrandLogoRendererProps {
  component: BrandLogoComponent;
}

export const BrandLogoRenderer: React.FC<BrandLogoRendererProps> = ({ component }) => {
  const { logoUrl, logoSize, link, openInNewTab, alignment, padding, backgroundColor, style } = component.settings;

  const logoStyle = style || 'default';
  const alignmentStyles = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  const logoElement = (
    <img
      src={logoUrl}
      alt="Brand Logo"
      style={{
        width: `${logoSize}px`,
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x150?text=Your+Logo';
      }}
    />
  );

  // Default Style
  if (logoStyle === 'default') {
    return (
      <div
        style={{
          padding: `${padding}px`,
          backgroundColor,
          display: 'flex',
          justifyContent: alignmentStyles[alignment],
          alignItems: 'center',
          width: '100%',
          lineHeight: 0, // Prevent extra space from inline elements
        }}
      >
        {link ? (
          <a
            href={link}
            target={openInNewTab ? '_blank' : '_self'}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            style={{ display: 'block', transition: 'opacity 0.2s', lineHeight: 0 }}
            className="hover:opacity-80"
          >
            {logoElement}
          </a>
        ) : (
          logoElement
        )}
      </div>
    );
  }

  // Modern Style
  if (logoStyle === 'modern') {
    return (
      <div
        style={{
          padding: `${padding}px`,
          backgroundColor,
          display: 'flex',
          justifyContent: alignmentStyles[alignment],
          alignItems: 'center',
          width: '100%',
          lineHeight: 0, // Prevent extra space from inline elements
        }}
      >
        <div
          className="relative"
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
            borderRadius: '16px',
            padding: '16px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            lineHeight: 0,
          }}
        >
          {link ? (
            <a
              href={link}
              target={openInNewTab ? '_blank' : '_self'}
              rel={openInNewTab ? 'noopener noreferrer' : undefined}
              style={{ display: 'block', transition: 'transform 0.2s', lineHeight: 0 }}
              className="hover:scale-105"
            >
              {logoElement}
            </a>
          ) : (
            logoElement
          )}
        </div>
      </div>
    );
  }

  // Minimal Style
  if (logoStyle === 'minimal') {
    return (
      <div
        style={{
          padding: `${padding}px`,
          backgroundColor,
          display: 'flex',
          justifyContent: alignmentStyles[alignment],
          alignItems: 'center',
          width: '100%',
          lineHeight: 0, // Prevent extra space from inline elements
        }}
      >
        <div style={{ opacity: 0.9, lineHeight: 0 }}>
          {link ? (
            <a
              href={link}
              target={openInNewTab ? '_blank' : '_self'}
              rel={openInNewTab ? 'noopener noreferrer' : undefined}
              style={{ display: 'block', transition: 'opacity 0.2s', lineHeight: 0 }}
              className="hover:opacity-70"
            >
              {logoElement}
            </a>
          ) : (
            logoElement
          )}
        </div>
      </div>
    );
  }

  // Card Style
  if (logoStyle === 'card') {
    return (
      <div
        style={{
          padding: `${padding}px`,
          backgroundColor,
          display: 'flex',
          justifyContent: alignmentStyles[alignment],
          alignItems: 'center',
          width: '100%',
          lineHeight: 0, // Prevent extra space from inline elements
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            lineHeight: 0,
          }}
        >
          {link ? (
            <a
              href={link}
              target={openInNewTab ? '_blank' : '_self'}
              rel={openInNewTab ? 'noopener noreferrer' : undefined}
              style={{ display: 'block', transition: 'transform 0.2s', lineHeight: 0 }}
              className="hover:scale-105"
            >
              {logoElement}
            </a>
          ) : (
            logoElement
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

